import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * Half the row is a lifetime account fact and half is windowed. `joinedAt`,
 * `walletAddress`, `sourceUtm`, the entry dates and the balances describe the
 * account; the counts and every money column describe `[from, to)`.
 *
 * Every money column is a LAG delta on `freeAfter + lockedAfter`, never
 * `SUM(amount)` — `amount` is a signed single-column delta whose lock/release
 * pairs double-count.
 *
 * `wins` derives from the ledger (distinct battle-arena campaigns with
 * net-positive COMPETITION_REWARD), not `HubUserCompetitionWin` — that table is
 * not written for bots. `duels` = `ArenaEntry.status = 'ASSIGNED_NOTIFIED'`.
 * A win rate over 100% is badged by the UI, never clamped here.
 */
export const ArenaOpsUserRowSchema = z.object({
  hubUserId: z.number().int(),
  nickname: z.string().nullable(),
  email: z.string().nullable(),
  /** `HubUser.defiAddress`. Null for an email-OTP account with no linked wallet. */
  walletAddress: z.string().nullable(),
  /** Hub account creation. NOT `firstEntryAt`, which is the first arena entry and can be months later. */
  joinedAt: z.iso.datetime(),
  /** First-touch `?utm=`, set once at signup. Null for accounts created before it was captured. */
  sourceUtm: z.string().nullable(),
  /** Country of the user's most recent `ArenaEntry` — a per-opt-in snapshot, not a profile field. */
  countryCode: z.string().nullable(),
  firstEntryAt: z.iso.datetime().nullable(),
  lastEntryAt: z.iso.datetime().nullable(),
  entries: z.number().int(),
  duels: z.number().int(),
  wins: z.number().int(),
  cancelled: z.number().int(),
  everBotEntry: z.boolean(),
  /** From the ledger alone: a ticket-funded entry writes MATCHMAKING_CONSUME with amount 0. */
  stakedUsdt: ArenaOpsUsdtSchema,
  depositedUsdt: ArenaOpsUsdtSchema,
  /** Positive magnitude — the ledger delta is negative and is negated at the source. */
  withdrawnUsdt: ArenaOpsUsdtSchema,
  /** COMPETITION_REWARD payout over the window; `wins` counts the campaigns behind it. */
  wonUsdt: ArenaOpsUsdtSchema,
  /** ADMIN_CREDIT net of ADMIN_DEBIT. Free tickets write no movement, so their value is in the ticket counts, not here. */
  grantedUsdt: ArenaOpsUsdtSchema,
  freeUsdt: ArenaOpsUsdtSchema,
  lockedUsdt: ArenaOpsUsdtSchema,
  totalUsdt: ArenaOpsUsdtSchema,
  activeTickets: z.number().int(),
  usedTickets: z.number().int(),
});
export type ArenaOpsUserRow = z.infer<typeof ArenaOpsUserRowSchema>;

export const ArenaOpsUsersResponseSchema = z.object({
  rows: z.array(ArenaOpsUserRowSchema),
  nextCursor: z.string().nullable(),
  totalCount: z.number().int(),
  /**
   * Anchor set is `CedeArenaVirtualBalance` ∪ `arena_tickets`. The legacy users
   * with an entry but no balance row are excluded — surfaced as a count so
   * nobody discovers the gap by noticing a missing user.
   */
  excludedLegacyUserCount: z.number().int(),
});
export type ArenaOpsUsersResponse = z.infer<typeof ArenaOpsUsersResponseSchema>;
